// ./src/pages/private/EmailTemplatesPage/EmailTemplatesPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack, Select, Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
  PremiumFeatureGate,
  FeatureBenefitsList,
} from "@/components/generic";
import {
  EmailTemplatesTable,
  EmailTemplatesEditModal,
  EmailTemplatesPreviewModal,
} from "@/components/features";
import { useEmailTemplates, useNumericFeature } from "@/hooks";
import { EmailTemplate, TEMPLATE_TYPES } from "@/utils/api";
import { useApp } from "@/contexts";
import {
  ENTITIES,
  FEATURE_CODES,
  DEFAULT_PAGE_SIZE,
  OPERATION_RIGHTS,
} from "@/config";

const templateTypeFilterOptions = [
  { value: "", label: "All Types" },
  { value: TEMPLATE_TYPES.NOTIFICATION, label: "Notification" },
  { value: TEMPLATE_TYPES.CONFIRMATION, label: "Confirmation" },
];

const EMAIL_TEMPLATES_BENEFITS = [
  "Create custom notification emails with your brand voice and messaging",
  "Design confirmation emails sent to form submitters",
  "Use Handlebars templating to include form data dynamically",
  "Set default templates per type for consistent communications",
  "Combine with custom layouts for complete brand control",
  "Preview templates with sample data before activating",
];

const VERIFICATION_NOTICE = {
  title: "Manual Verification Required",
  message: `All custom email templates are manually reviewed before activation to prevent misuse. 
Please allow up to 48 hours (excluding weekends and holidays) for verification. 
For company-branded templates, your account must be registered with a company email (@yourcompany.com), 
and we may contact your organization through official channels for confirmation. 
Please ensure your profile contains your correct first and last name.`,
};

export const EmailTemplatesPage: React.FC<PageProps> = (props: PageProps) => {
  const { user, hasRightTo } = useApp();
  const userId = user?.id;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ emailTemplateId?: string }>();

  // Check if email templates feature is enabled
  const { isEnabled: isEmailTemplatesEnabled } = useNumericFeature(
    FEATURE_CODES.MAX_CUSTOM_EMAIL_TEMPLATES
  );

  // Parse URL to determine which modal to show
  const emailTemplateIdFromUrl = params.emailTemplateId;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode =
    !!emailTemplateIdFromUrl && location.pathname.endsWith("/edit");
  const isPreviewMode =
    !!emailTemplateIdFromUrl && location.pathname.endsWith("/preview");

  const canCreate = hasRightTo(
    ENTITIES.EMAIL_TEMPLATES,
    OPERATION_RIGHTS.CREATE
  );

  // Use the email templates hook for all data management
  const {
    emailTemplates,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    sortBy,
    isCreating,
    isUpdating,
    isPreviewing,
    isLoadingEmailTemplate,
    removingEmailTemplateId,
    list,
    loadMore,
    refresh,
    get,
    preview,
    publish,
    setFilters,
    setSortBy,
    create,
    update,
    remove,
    lastError,
    clearError,
  } = useEmailTemplates({
    autoLoad: isEmailTemplatesEnabled,
    userId,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [emailTemplateToDelete, setEmailTemplateToDelete] =
    useState<EmailTemplate | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [templateTypeFilter, setTemplateTypeFilter] = useState<string>("");
  const [editingEmailTemplate, setEditingEmailTemplate] =
    useState<EmailTemplate | null>(null);
  const [previewingEmailTemplate, setPreviewingEmailTemplate] =
    useState<EmailTemplate | null>(null);
  const [editedEmailTemplateId, setEditedEmailTemplateId] = useState<
    string | null
  >(null);
  const [publishingEmailTemplateId, setPublishingEmailTemplateId] = useState<
    string | null
  >(null);

  // Load email template when in edit or preview mode
  // Always load full data as list items may not contain htmlContent, textContent, etc.
  useEffect(() => {
    if (
      (isEditMode || isPreviewMode) &&
      emailTemplateIdFromUrl &&
      isEmailTemplatesEnabled
    ) {
      get(emailTemplateIdFromUrl);
    }
  }, [
    isEditMode,
    isPreviewMode,
    emailTemplateIdFromUrl,
    get,
    isEmailTemplatesEnabled,
  ]);

  // Update editing/previewing email template when we get data
  useEffect(() => {
    if (isEditMode && emailTemplateIdFromUrl) {
      const emailTemplate = emailTemplates.find(
        (t) => t.id === emailTemplateIdFromUrl
      );
      if (emailTemplate) {
        setEditingEmailTemplate(emailTemplate);
      }
    } else if (isPreviewMode && emailTemplateIdFromUrl) {
      const emailTemplate = emailTemplates.find(
        (t) => t.id === emailTemplateIdFromUrl
      );
      if (emailTemplate) {
        setPreviewingEmailTemplate(emailTemplate);
      }
    } else if (isAddMode && editingEmailTemplate?.id) {
      // Keep the duplicated template data for add mode
    } else if (!isAddMode && !isEditMode) {
      setEditingEmailTemplate(null);
    }

    if (!isPreviewMode) {
      setPreviewingEmailTemplate(null);
    }
  }, [
    isEditMode,
    isPreviewMode,
    isAddMode,
    emailTemplateIdFromUrl,
    emailTemplates,
  ]);

  // Build filters from search and type filter
  const buildFilters = useCallback((search: string, typeFilter: string) => {
    const newFilters: any = {};

    if (search.trim()) {
      newFilters.searchKeyword = search.trim();
    }

    if (typeFilter) {
      newFilters.templateType = [typeFilter];
    }

    return newFilters;
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      const newFilters = buildFilters(value, templateTypeFilter);
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [sortBy, setFilters, list, buildFilters, templateTypeFilter]
  );

  // Handle template type filter change
  const handleTemplateTypeFilterChange = useCallback(
    (value: string | null) => {
      const typeFilter = value || "";
      setTemplateTypeFilter(typeFilter);
      const newFilters = buildFilters(searchValue, typeFilter);
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [sortBy, setFilters, list, buildFilters, searchValue]
  );

  // Handle add email template button click
  const handleAddEmailTemplate = useCallback(() => {
    clearError();
    setEditingEmailTemplate(null);
    navigate("/email-templates/add");
  }, [navigate, clearError]);

  // Handle edit email template
  const handleEditEmailTemplate = useCallback(
    (emailTemplate: EmailTemplate) => {
      clearError();
      if (emailTemplate.id) {
        // Don't pre-populate from list as it may not have full data (htmlContent, etc.)
        // The useEffect will load full data and show skeleton while loading
        navigate(`/email-templates/${emailTemplate.id}/edit`);
      }
    },
    [navigate, clearError]
  );

  // Handle preview email template - opens preview modal
  const handlePreviewEmailTemplate = useCallback(
    (emailTemplate: EmailTemplate) => {
      clearError();
      if (emailTemplate.id) {
        // Don't pre-populate from list as it may not have full data
        // The preview modal will load full preview data
        navigate(`/email-templates/${emailTemplate.id}/preview`);
      }
    },
    [navigate, clearError]
  );

  // Handle duplicate email template
  const handleDuplicateEmailTemplate = useCallback(
    (emailTemplate: EmailTemplate) => {
      clearError();
      setEditingEmailTemplate({
        ...emailTemplate,
        id: undefined,
        accountId: undefined,
        name: `${emailTemplate.name} (Copy)`,
        isDefault: false,
      });
      navigate("/email-templates/add");
    },
    [navigate, clearError]
  );

  // Handle publish email template
  const handlePublishEmailTemplate = useCallback(
    async (emailTemplate: EmailTemplate) => {
      if (emailTemplate.id) {
        setPublishingEmailTemplateId(emailTemplate.id);
        const result = await publish(emailTemplate.id);
        setPublishingEmailTemplateId(null);
        if (result) {
          setEditedEmailTemplateId(result.id || null);
        }
      }
    },
    [publish]
  );

  // Handle remove email template button click
  const handleRemoveEmailTemplateClick = useCallback(
    (emailTemplate: EmailTemplate) => {
      setEmailTemplateToDelete(emailTemplate);
    },
    []
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (emailTemplateToDelete?.id) {
      const success = await remove(emailTemplateToDelete.id);
      if (success) {
        setEmailTemplateToDelete(null);
        if (emailTemplateIdFromUrl === emailTemplateToDelete.id) {
          navigate("/email-templates");
        }
      }
    }
  }, [emailTemplateToDelete, remove, emailTemplateIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setEmailTemplateToDelete(null);
  }, []);

  // Handle email template edit modal close
  const handleEmailTemplateEditModalClose = useCallback(() => {
    setEditingEmailTemplate(null);
    clearError();
    navigate("/email-templates");
  }, [navigate, clearError]);

  // Handle email template preview modal close
  const handleEmailTemplatePreviewModalClose = useCallback(() => {
    setPreviewingEmailTemplate(null);
    clearError();
    navigate("/email-templates");
  }, [navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle email template creation
  const handleCreateEmailTemplate = useCallback(
    async (data: any) => {
      const result = await create(data);
      if (result) {
        setEditedEmailTemplateId(result.id || null);
        navigate("/email-templates");
      }
      return result;
    },
    [create, navigate]
  );

  // Handle email template update
  const handleUpdateEmailTemplate = useCallback(
    async (emailTemplateId: string, data: any) => {
      const result = await update(emailTemplateId, data);
      if (result) {
        setEditedEmailTemplateId(result.id || null);
      }
      return result;
    },
    [update]
  );

  // Handle email template preview request
  const handlePreviewEmailTemplateContent = useCallback(
    async (emailTemplateId: string, sampleData?: string) => {
      return await preview(emailTemplateId, sampleData);
    },
    [preview]
  );

  // Determine if edit modal should show loading
  const isEditModalLoading =
    isEditMode && emailTemplateIdFromUrl
      ? isLoadingEmailTemplate(emailTemplateIdFromUrl)
      : false;

  return (
    <DashboardLayout {...props}>
      <PremiumFeatureGate
        featureCode={FEATURE_CODES.MAX_CUSTOM_EMAIL_TEMPLATES}
        featureTitle="Custom Email Templates"
        forceEnabled={isEmailTemplatesEnabled}
        featureDescription={
          <FeatureBenefitsList
            benefits={EMAIL_TEMPLATES_BENEFITS}
            notice={VERIFICATION_NOTICE}
          />
        }
      >
        <Stack gap="md">
          {/* Verification notice */}
          <Alert
            icon={<IconInfoCircle size={18} />}
            title={VERIFICATION_NOTICE.title}
            color="blue"
            variant="light"
          >
            {VERIFICATION_NOTICE.message}
          </Alert>

          {/* Page toolbar with actions and search */}
          <AppToolbar
            preset="page-header"
            sections={[
              {
                id: "actions",
                items: [
                  <AppButton
                    key="add-email-template"
                    iconName="Plus"
                    onClick={handleAddEmailTemplate}
                    disabled={
                      isCreating ||
                      !!removingEmailTemplateId ||
                      loading ||
                      !canCreate
                    }
                  >
                    Add
                  </AppButton>,
                  <AppButton
                    key="refresh"
                    variant="secondary"
                    iconName="Refresh"
                    onClick={handleRefresh}
                    disabled={
                      isCreating || !!removingEmailTemplateId || loading
                    }
                  />,
                ],
                align: "left",
                gap: "sm",
              },
              {
                id: "filters",
                items: [
                  <Select
                    key="type-filter"
                    placeholder="Filter by type"
                    data={templateTypeFilterOptions}
                    value={templateTypeFilter}
                    onChange={handleTemplateTypeFilterChange}
                    clearable={false}
                    style={{ minWidth: 160 }}
                    size="sm"
                  />,
                  <SearchInput
                    key="search"
                    placeholder="Search templates..."
                    value={searchValue}
                    onChange={(value) => handleSearchChange(value)}
                    style={{ minWidth: 250 }}
                  />,
                ],
                align: "right",
                gap: "sm",
              },
            ]}
          />

          {/* Email templates table */}
          <EmailTemplatesTable
            emailTemplates={emailTemplates}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onLoadMore={loadMore}
            onEditEmailTemplate={handleEditEmailTemplate}
            onDuplicateEmailTemplate={handleDuplicateEmailTemplate}
            onPreviewEmailTemplate={handlePreviewEmailTemplate}
            onRemoveEmailTemplate={handleRemoveEmailTemplateClick}
            onPublishEmailTemplate={handlePublishEmailTemplate}
            removingEmailTemplateId={removingEmailTemplateId}
            publishingEmailTemplateId={publishingEmailTemplateId}
            editedItemId={editedEmailTemplateId}
          />

          {/* Empty state when no email templates */}
          <EmptyState
            display={isEmpty && !loading}
            title="No email templates yet"
            description="Create your first email template to customize notifications and confirmations"
            actionIcon="Plus"
            actionLabel="Add Template"
            onAction={handleAddEmailTemplate}
          />
        </Stack>

        {/* Email template edit modal for create/edit */}
        <EmailTemplatesEditModal
          opened={isAddMode || isEditMode}
          onClose={handleEmailTemplateEditModalClose}
          emailTemplate={
            isEditMode
              ? editingEmailTemplate
              : isAddMode && editingEmailTemplate
              ? editingEmailTemplate
              : null
          }
          onCreateEmailTemplate={handleCreateEmailTemplate}
          onUpdateEmailTemplate={handleUpdateEmailTemplate}
          isCreating={isCreating}
          isUpdating={isUpdating}
          isLoading={isEditModalLoading}
          lastError={lastError}
          onClearError={clearError}
        />

        {/* Email template preview modal */}
        <EmailTemplatesPreviewModal
          opened={isPreviewMode}
          onClose={handleEmailTemplatePreviewModalClose}
          emailTemplate={previewingEmailTemplate}
          onPreviewEmailTemplate={handlePreviewEmailTemplateContent}
          isPreviewing={isPreviewing}
          lastError={lastError}
          onClearError={clearError}
        />

        {/* Delete confirmation modal */}
        <ConfirmModal
          opened={!!emailTemplateToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Email Template"
          message={`Are you sure you want to delete "${emailTemplateToDelete?.name}"?`}
          details="This action cannot be undone. Forms using this template will fall back to the default template."
          confirmInProgress={
            removingEmailTemplateId === emailTemplateToDelete?.id
          }
          confirmLabel="Delete"
          type="danger"
        />
      </PremiumFeatureGate>
    </DashboardLayout>
  );
};
